import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Config from 'config/config';
import { ToastService } from 'src/app/services/toast.service';
import { ssoConnectionsService } from 'src/app/shared/vive-api/sso-connections.service';
import {
  SsoConnectionDto,
  SsoConnectionsUpdateRequestDto
} from 'src/app/shared/vive-api/vive-api-models';

import {
  SSO_PROTOCOL_LABELS,
  SSO_SAVE_FAILED,
  SSO_STATUS_CHIPS,
  confirmSsoDisable,
  hasDomainAndMapping,
  loadClientNames,
  ssoApiErrorMessage,
  ssoConnectionForLabel,
  ssoLoadErrorMessage
} from '../sso-connections';

type SettingsCard = 'connection' | 'provider' | 'attributes';

// The update request's string settings that are never NULL.
type TextSetting =
  | 'name'
  | 'oidcScopes'
  | 'groupsClaim'
  | 'firstNameAttribute'
  | 'lastNameAttribute';

// The update request's string settings whose unset value is NULL.
type ClearableSetting =
  | 'oidcIssuerUrl'
  | 'oidcClientId'
  | 'samlIdpEntityId'
  | 'samlIdpSsoUrl'
  | 'emailAttribute';

interface ProviderDraft {
  oidcIssuerUrl: string;
  oidcClientId: string;
  oidcScopes: string;
  samlIdpEntityId: string;
  samlIdpSsoUrl: string;
  samlIdpCerts: string[];
  samlRequireSignedResponse: boolean;
}

interface AttributesDraft {
  groupsClaim: string;
  emailAttribute: string;
  firstNameAttribute: string;
  lastNameAttribute: string;
}

interface OurSideUrl {
  label: string;
  url: string;
}

// The API's limit on a connection's signing certificates.
const MAX_CERTIFICATES = 5;

const ALL_SETTINGS_CARDS: readonly SettingsCard[] = [
  'connection',
  'provider',
  'attributes'
];

const certificatesOf = (certificates: readonly string[]): string[] =>
  certificates.map((pem) => pem.trim()).filter((pem) => pem !== '');

@Component({
  selector: 'app-admin-admin-sso-connection',
  templateUrl: './admin-admin-sso-connection.component.html',
  styleUrl: './admin-admin-sso-connection.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminAdminSsoConnectionComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);

  public readonly protocolLabels = SSO_PROTOCOL_LABELS;
  public readonly statusChips = SSO_STATUS_CHIPS;
  public readonly maxCertificates = MAX_CERTIFICATES;

  public connection: SsoConnectionDto | null = null;
  public loading = true;
  public loadError: string | null = null;
  public forLabel = '';
  public providerEyebrow = '';
  public ourSideEyebrow = '';
  public ourSide: OurSideUrl[] = [];
  public copiedLabel: string | null = null;

  public statusError: string | null = null;
  public changingStatus = false;
  public savingCard: SettingsCard | null = null;
  public cardErrors: Partial<Record<SettingsCard, string>> = {};

  public nameDraft = '';
  public requireSsoDraft = false;
  public readonly requireSsoHint =
    "Users this connection serves can't sign in or reset with a password. Break-glass users are exempt.";
  public provider: ProviderDraft = {
    oidcIssuerUrl: '',
    oidcClientId: '',
    oidcScopes: '',
    samlIdpEntityId: '',
    samlIdpSsoUrl: '',
    samlIdpCerts: [''],
    samlRequireSignedResponse: false
  };
  public attributes: AttributesDraft = {
    groupsClaim: '',
    emailAttribute: '',
    firstNameAttribute: '',
    lastNameAttribute: ''
  };

  public get canActivate(): boolean {
    return this.connection !== null && hasDomainAndMapping(this.connection);
  }

  /**
   * Whether the API returned `requireSso` at all. An API from before the field
   * would read a missing value as off and reject a body that sends it, so the
   * setting is shown and sent only when the connection carries it.
   */
  public get supportsRequireSso(): boolean {
    return this.connection !== null && 'requireSso' in this.connection;
  }

  public async ngOnInit(): Promise<void> {
    const connectionId = Number(
      this.route.snapshot.paramMap.get('connectionId')
    );

    try {
      // The API has no get-by-id; every route answers with whole connections.
      const connection = (await ssoConnectionsService.findAll()).find(
        ({ id }) => id === connectionId
      );

      if (connection === undefined) {
        this.loadError = "That SSO connection doesn't exist.";
        return;
      }

      const clientNames = await loadClientNames(
        connection.CompanyId === null ? [] : [connection.CompanyId]
      );
      const isStaff = connection.scope === 'staff';
      this.forLabel = ssoConnectionForLabel(connection, clientNames);
      this.providerEyebrow = isStaff
        ? 'Identity provider'
        : "Identity provider (from the client's IT team)";
      this.ourSideEyebrow = isStaff
        ? "Our side · send to VIVE's IT team"
        : "Our side · send to the client's IT team";
      this.ourSide = this.buildOurSide(connection);
      this.connection = connection;
      this.resetDrafts(ALL_SETTINGS_CARDS);
    } catch (error) {
      this.loadError = ssoLoadErrorMessage(error);
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  public settingsChanges(card: SettingsCard): SsoConnectionsUpdateRequestDto {
    const { connection } = this;
    const changes: SsoConnectionsUpdateRequestDto = {};

    if (connection === null) {
      return changes;
    }

    const setIfChanged = (
      key: TextSetting,
      draft: string,
      saved: string | null
    ): void => {
      const value = draft.trim();
      if (value !== (saved ?? '')) {
        changes[key] = value;
      }
    };
    // A blank sends NULL, so the API stores the setting as unset rather than
    // as ''. A blank over a stored '' is no change.
    const setOrClearIfChanged = (
      key: ClearableSetting,
      draft: string,
      saved: string | null
    ): void => {
      const value = draft.trim();
      if (value !== (saved ?? '')) {
        changes[key] = value === '' ? null : value;
      }
    };
    const isSaml = connection.protocol === 'saml';

    if (card === 'connection') {
      setIfChanged('name', this.nameDraft, connection.name);

      if (
        this.supportsRequireSso &&
        this.requireSsoDraft !== connection.requireSso
      ) {
        changes.requireSso = this.requireSsoDraft;
      }
    }

    if (card === 'provider' && !isSaml) {
      setOrClearIfChanged(
        'oidcIssuerUrl',
        this.provider.oidcIssuerUrl,
        connection.oidcIssuerUrl
      );
      setOrClearIfChanged(
        'oidcClientId',
        this.provider.oidcClientId,
        connection.oidcClientId
      );
      setIfChanged(
        'oidcScopes',
        this.provider.oidcScopes,
        connection.oidcScopes
      );
    }

    if (card === 'provider' && isSaml) {
      setOrClearIfChanged(
        'samlIdpEntityId',
        this.provider.samlIdpEntityId,
        connection.samlIdpEntityId
      );
      setOrClearIfChanged(
        'samlIdpSsoUrl',
        this.provider.samlIdpSsoUrl,
        connection.samlIdpSsoUrl
      );

      const certificates = certificatesOf(this.provider.samlIdpCerts);
      if (
        JSON.stringify(certificates) !==
        JSON.stringify(certificatesOf(connection.samlIdpCerts ?? []))
      ) {
        changes.samlIdpCerts = certificates;
      }

      if (
        this.provider.samlRequireSignedResponse !==
        connection.samlRequireSignedResponse
      ) {
        changes.samlRequireSignedResponse =
          this.provider.samlRequireSignedResponse;
      }
    }

    if (card === 'attributes') {
      setIfChanged(
        'groupsClaim',
        this.attributes.groupsClaim,
        connection.groupsClaim
      );
      setIfChanged(
        'firstNameAttribute',
        this.attributes.firstNameAttribute,
        connection.firstNameAttribute
      );
      setIfChanged(
        'lastNameAttribute',
        this.attributes.lastNameAttribute,
        connection.lastNameAttribute
      );
      // OIDC reads the standard email claim and never this setting.
      if (isSaml) {
        setOrClearIfChanged(
          'emailAttribute',
          this.attributes.emailAttribute,
          connection.emailAttribute
        );
      }
    }

    return changes;
  }

  public canSave(card: SettingsCard): boolean {
    return (
      this.savingCard === null &&
      this.isComplete(card) &&
      Object.keys(this.settingsChanges(card)).length > 0
    );
  }

  public async saveSettings(card: SettingsCard): Promise<void> {
    const { connection } = this;

    if (connection === null || !this.canSave(card)) {
      return;
    }

    this.savingCard = card;
    this.cardErrors = { ...this.cardErrors, [card]: undefined };
    this.cdr.markForCheck();

    try {
      const updated = await ssoConnectionsService.updateById({
        pathParams: { connectionId: connection.id },
        data: this.settingsChanges(card)
      });
      // A PATCH changes settings only; status, domains and mappings belong to
      // other requests (see mergeConnection).
      const { status, Domains, RoleMappings } = this.connection ?? updated;
      this.connection = { ...updated, status, Domains, RoleMappings };
      // Only this card: another card's unsaved edits stay as they are.
      this.resetDrafts([card]);
      this.toastService.addSuccessToast('Saved', 'Your changes are saved.');
    } catch (error) {
      this.cardErrors = {
        ...this.cardErrors,
        [card]: ssoApiErrorMessage(error, SSO_SAVE_FAILED)
      };
    } finally {
      this.savingCard = null;
      this.cdr.markForCheck();
    }
  }

  public addCertificate(): void {
    if (this.provider.samlIdpCerts.length < MAX_CERTIFICATES) {
      this.provider.samlIdpCerts = [...this.provider.samlIdpCerts, ''];
    }
  }

  public removeCertificate(index: number): void {
    const remaining = this.provider.samlIdpCerts.filter((_, i) => i !== index);
    this.provider.samlIdpCerts = remaining.length > 0 ? remaining : [''];
  }

  public async activate(): Promise<void> {
    const { connection } = this;

    if (connection === null) {
      return;
    }

    await this.changeStatus(async () => {
      const updated = await ssoConnectionsService.activate({
        pathParams: { connectionId: connection.id }
      });
      this.toastService.addSuccessToast(
        'Activated',
        `${updated.name} is active.`
      );

      return updated;
    });
  }

  public async disable(): Promise<void> {
    const { connection } = this;

    if (
      connection === null ||
      !(await confirmSsoDisable(this.modalService, connection.name))
    ) {
      return;
    }

    await this.changeStatus(async () => {
      const updated = await ssoConnectionsService.disable({
        pathParams: { connectionId: connection.id }
      });
      this.toastService.addSuccessToast(
        'Disabled',
        `${updated.name} is disabled.`
      );

      return updated;
    });
  }

  public onDomainsSaved(updated: SsoConnectionDto): void {
    this.mergeConnection({ Domains: updated.Domains });
  }

  public onRoleMappingsSaved(updated: SsoConnectionDto): void {
    this.mergeConnection({ RoleMappings: updated.RoleMappings });
  }

  public async copy(row: OurSideUrl): Promise<void> {
    try {
      await navigator.clipboard.writeText(row.url);
      this.copiedLabel = row.label;
    } catch {
      this.copiedLabel = null;
    }

    this.cdr.markForCheck();
  }

  private isComplete(card: SettingsCard): boolean {
    const { connection } = this;

    if (connection === null) {
      return false;
    }

    if (card === 'connection') {
      return this.nameDraft.trim() !== '';
    }

    if (card === 'attributes') {
      const { groupsClaim, firstNameAttribute, lastNameAttribute } =
        this.attributes;

      return (
        groupsClaim.trim() !== '' &&
        firstNameAttribute.trim() !== '' &&
        lastNameAttribute.trim() !== ''
      );
    }

    // The API accepts a blank Scopes, and sign-in then asks for `openid` alone,
    // which usually leaves the email out of the ID token.
    if (card === 'provider' && connection.protocol === 'oidc') {
      return this.provider.oidcScopes.trim() !== '';
    }

    return true;
  }

  private async changeStatus(
    change: () => Promise<SsoConnectionDto>
  ): Promise<void> {
    this.changingStatus = true;
    this.statusError = null;
    this.cdr.markForCheck();

    try {
      const { status } = await change();
      this.mergeConnection({ status });
    } catch (error) {
      this.statusError = ssoApiErrorMessage(error, SSO_SAVE_FAILED);
    } finally {
      this.changingStatus = false;
      this.cdr.markForCheck();
    }
  }

  // Every route answers with the whole connection, but the cards and the header
  // save independently, so responses can land out of order. Each handler takes
  // only the part its own request changed: a slower response must not put back
  // what a newer one replaced.
  private mergeConnection(part: Partial<SsoConnectionDto>): void {
    if (this.connection !== null) {
      this.connection = { ...this.connection, ...part };
    }

    this.cdr.markForCheck();
  }

  private resetDrafts(cards: readonly SettingsCard[]): void {
    const { connection } = this;

    if (connection === null) {
      return;
    }

    if (cards.includes('connection')) {
      this.nameDraft = connection.name;
      // Missing, not false, on an API from before the field (supportsRequireSso).
      this.requireSsoDraft = connection.requireSso ?? false;
    }

    if (cards.includes('provider')) {
      const certificates = connection.samlIdpCerts ?? [];
      this.provider = {
        oidcIssuerUrl: connection.oidcIssuerUrl ?? '',
        oidcClientId: connection.oidcClientId ?? '',
        oidcScopes: connection.oidcScopes,
        samlIdpEntityId: connection.samlIdpEntityId ?? '',
        samlIdpSsoUrl: connection.samlIdpSsoUrl ?? '',
        samlIdpCerts: certificates.length > 0 ? [...certificates] : [''],
        samlRequireSignedResponse: connection.samlRequireSignedResponse
      };
    }

    if (cards.includes('attributes')) {
      this.attributes = {
        groupsClaim: connection.groupsClaim,
        emailAttribute: connection.emailAttribute ?? '',
        firstNameAttribute: connection.firstNameAttribute,
        lastNameAttribute: connection.lastNameAttribute
      };
    }
  }

  // The API builds the same URLs from auth:sso:publicBaseUrl, which is this app's
  // viveApiUrl in every environment.
  private buildOurSide(connection: SsoConnectionDto): OurSideUrl[] {
    const base = `${Config.getEnvironmentConfig().viveApiUrl}/api/v3/auth/sso`;

    if (connection.protocol === 'oidc') {
      return [
        { label: 'Redirect URI', url: `${base}/oidc/callback` },
        { label: 'JWKS URL', url: `${base}/jwks` }
      ];
    }

    // The SP entity id IS the metadata URL.
    const metadata = `${base}/saml/${connection.id}/metadata`;

    return [
      { label: 'SP entity ID', url: metadata },
      { label: 'ACS URL', url: `${base}/saml/acs` },
      { label: 'Metadata URL', url: metadata }
    ];
  }
}
